import { createSelector } from '@ngrx/store';
import { Selector } from '@ngrx/store/src/models';

import { memoizeSelector, namedProjector } from '../selector-utils';
import { MetaState, Reference } from '../types';
import { createSelectorWithReferences } from '../utils';
import { BaseEntity, BaseSelector, EntityState, ID, MetaScopeOptions, Scope } from './types';
import { resolveScope } from './utils';

type MetaSelectorProps = ID | { scope: Scope };

const getMetaSelector = <T extends BaseEntity, K extends keyof MetaState>(
  metaProp: K,
  baseSelector: BaseSelector<T>,
  aliasesSelector: Selector<any, Record<string, ID>>,
  namespace: string,
) =>
  memoizeSelector((scopeOrEntityId?: MetaSelectorProps) =>
    createSelector(
      baseSelector,
      aliasesSelector,
      namedProjector(
        `${namespace}${metaProp}`,
        (state: EntityState<T>, aliases: Record<string, string>) => {
          if (!scopeOrEntityId || (<{ scope: Scope }>scopeOrEntityId)?.scope) {
            const scope = resolveScope((<{ scope: Scope }>scopeOrEntityId)?.scope);
            return state.scopeMeta[scope]?.[metaProp];
          } else {
            const entityId = aliases[<ID>scopeOrEntityId] || <ID>scopeOrEntityId;
            return state.entityMetaMap[entityId]?.[metaProp];
          }
        },
      ),
    ),
  );

// eslint-disable-next-line max-lines-per-function
export const getSelectors = <T extends BaseEntity>(
  baseSelector: BaseSelector<T>,
  namespace: string,
  references: Reference<T>[] | null,
) => {
  const entityMap = createSelector(baseSelector, state => state.entityMap);
  const aliases = createSelector(baseSelector, state => state.aliases);
  const byId = memoizeSelector((id: string | number) =>
    createSelector(
      entityMap,
      aliases,
      namedProjector(
        `${namespace}ById`,
        (entities: EntityState<T>['entityMap'], aliases: EntityState<T>['aliases']) => {
          const entityId = aliases[id] || id;
          return entities[entityId] || null;
        },
      ),
    ),
  );

  const isLoaded = getMetaSelector<T, 'loaded'>('loaded', baseSelector, aliases, namespace);
  const isPending = getMetaSelector<T, 'pending'>('pending', baseSelector, aliases, namespace);
  const isFailed = getMetaSelector<T, 'failed'>('failed', baseSelector, aliases, namespace);
  const error = getMetaSelector<T, 'error'>('error', baseSelector, aliases, namespace);

  const meta = memoizeSelector((idOrScope: MetaSelectorProps) =>
    createSelector(
      isLoaded(idOrScope),
      isPending(idOrScope),
      isFailed(idOrScope),
      error(idOrScope),
      namedProjector(
        `${namespace}Meta`,
        (isLoaded: boolean, isPending: boolean, isFailed: boolean, error: any) => ({
          isLoaded,
          isPending,
          isFailed,
          error,
        }),
      ),
    ),
  );

  const entities = memoizeSelector((props?: MetaScopeOptions) =>
    createSelector(
      baseSelector,
      namedProjector(`${namespace}Entities`, (state: EntityState<T>) => {
        const scope = resolveScope(props?.scope);
        return (state && state.scope[scope]?.map(id => state.entityMap[id]!)) || [];
      }),
    ),
  );

  const pagination = memoizeSelector((props?: MetaScopeOptions) =>
    createSelector(
      baseSelector,
      namedProjector(`${namespace}Pagination`, (state: EntityState<T>) => {
        const scope = resolveScope(props?.scope);
        return state.scopePagination[scope];
      }),
    ),
  );

  return {
    isLoaded,
    isPending,
    isFailed,
    error,
    meta,
    pagination,
    byId,
    byIdWithReferences: memoizeSelector((id: number) =>
      createSelectorWithReferences(
        byId(id),
        references,
        namedProjector(`${namespace}ByIdWithReferences`, state => state),
      ),
    ),
    entities,
    entitiesWithPagination: memoizeSelector((props?: MetaScopeOptions) =>
      createSelector(pagination(props), entities(props), (pagination, entities) => ({
        meta: pagination,
        rows: entities,
      })),
    ),
    entitiesWithReferences: memoizeSelector((props?: MetaScopeOptions) =>
      createSelectorWithReferences(
        entities(props),
        references,
        namedProjector(`${namespace}EntitiesWithReferences`, entities => entities),
      ),
    ),
  };
};
